import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Clock, Star, PlayCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: number; // minutes
  thumbnail: string;
  rating: number;
}

export function CourseCatalog() {
  const [courses, setCourses] = useKV<Course[]>("lms-courses", [
    {
      id: "1",
      title: "React Fundamentals",
      description: "Learn the basics of React development including hooks and components.",
      category: "Technical",
      difficulty: "beginner",
      duration: 120,
      thumbnail: "https://placehold.co/600x400/61dafb/white?text=React",
      rating: 4.8
    },
    {
      id: "2",
      title: "Effective Communication",
      description: "Master the art of workplace communication and leadership.",
      category: "Soft Skills",
      difficulty: "intermediate",
      duration: 60,
      thumbnail: "https://placehold.co/600x400/ff9900/white?text=Comms",
      rating: 4.5
    }
  ]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEnroll = (courseId: string) => {
    toast.success("Enrolled successfully!");
    // logic to add to user enrollments
  };

  return (
    <div className="space-y-6">
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input 
          placeholder="Search courses..." 
          className="pl-9" 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCourses.map((course) => (
          <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-all group">
            <div className="aspect-video bg-slate-100 relative">
              <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="secondary" className="gap-2" onClick={() => handleEnroll(course.id)}>
                  <PlayCircle className="w-5 h-5" /> Preview
                </Button>
              </div>
            </div>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <Badge variant="outline">{course.category}</Badge>
                <div className="flex items-center text-amber-500 text-xs font-bold gap-1">
                  <Star weight="fill" /> {course.rating}
                </div>
              </div>
              <CardTitle className="line-clamp-1">{course.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 line-clamp-2">{course.description}</p>
              <div className="flex items-center gap-4 mt-4 text-xs text-slate-400">
                <div className="flex items-center gap-1">
                  <Clock /> {course.duration} mins
                </div>
                <div className="capitalize">
                  {course.difficulty}
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full" onClick={() => handleEnroll(course.id)}>Enroll Now</Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}
